// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import type { Dispatch, ServerStatus } from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import BrowseServerFoldersDialog from 'view/App/DialogPanels/BrowseServerFoldersDialog';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  getActivePanelId,
  getGlobalSettings,
  getServerStatus,
  getLocale,
} from 'state/selectors';
import { setMobileUploadSettings } from 'state/commands/adminPropertiesCommands';
import {
  BROWSE_SERVER_FOLDERS_PANEL_ID,
  SERVER_STATE_STOPPED,
} from 'util/constants';
import { GLOBAL_SETTING } from 'util/serverUtils';
import type {
  GlobalSettings,
  MobileUploadSettings,
} from 'state/types/serverTypes';

type Props = {
  intl: IntlShape,
  globalSettings: GlobalSettings,
  setMobileUploadSettings: Function,
  activePanelId?: string,
  getActivePanelId: typeof getActivePanelId,
  showDialogPanel: typeof showDialogPanel,
  serverStatus: ServerStatus,
  currentLocale: string,
};

type State = {
  isWaitingForResponse: boolean,
  activeBrowsingFolder?: string,
};

class MobilePage extends Component<Props, State> {
  setMobileUploadSettings: Function;
  handleToggleCheckbox: Function;
  handleBrowseForDirectory: Function;
  handlePathSelected: Function;

  constructor() {
    super();
    this.handleToggleCheckbox = this.handleToggleCheckbox.bind(this);
    this.handleBrowseForDirectory = this.handleBrowseForDirectory.bind(this);
    this.handlePathSelected = this.handlePathSelected.bind(this);
  }

  handleToggleCheckbox(event) {
    const { setMobileUploadSettings, currentLocale, intl } = this.props;

    const settings: MobileUploadSettings = {
      [event.target.id]: event.target.checked,
      locale: currentLocale,
      catalogName: intl.formatMessage(localizedStrings.MOBILE_CATALOG_NAME),
    };

    setMobileUploadSettings(settings);
  }

  handleBrowseForDirectory(event) {
    const key = event.target.id;
    this.setState({ activeBrowsingFolder: key });
    this.props.showDialogPanel(BROWSE_SERVER_FOLDERS_PANEL_ID);
  }

  handlePathSelected(path) {
    const { globalSettings, setMobileUploadSettings } = this.props;
    const settings: MobileUploadSettings = {
      [GLOBAL_SETTING.MOBILE_UPLOADS_FOLDER]: path,
      [GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG]: !!globalSettings[
        GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG
      ],
    };
    setMobileUploadSettings(settings);
    this.setState({ activeBrowsingFolder: undefined });
  }

  render() {
    const {
      globalSettings,
      intl: { formatMessage },
      activePanelId,
      serverStatus,
    } = this.props;
    const isMobileEnabled =
      globalSettings[GLOBAL_SETTING.MOBILE_UPLOADS_ENABLED] === 'true';
    const hasUploadCatalog =
      globalSettings[GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG] !== '';
    const isStopped = serverStatus.runState === SERVER_STATE_STOPPED;

    return (
      <div
        className="page-contents columns is-gapless"
        data-test-name="mobile-uploads-page"
      >
        <div className="column">
          <header className="page-header">
            <FormattedMessage {...localizedStrings.MOBILE_TITLE} />
          </header>
          <section className="section page-contents-textured portfolio-form-container mobile-uploads">
            <div className="box">
              <div>
                <div
                  className="field is-flex"
                  style={{
                    justifyContent: 'left',
                  }}
                >
                  <input
                    checked={isMobileEnabled}
                    data-test-name="mobile-enabled"
                    disabled={isStopped}
                    className="is-checkradio is-medium"
                    id={GLOBAL_SETTING.MOBILE_UPLOADS_ENABLED}
                    onChange={this.handleToggleCheckbox}
                    name={GLOBAL_SETTING.MOBILE_UPLOADS_ENABLED}
                    type="checkbox"
                  />
                  <label
                    className="label is-medium"
                    htmlFor={GLOBAL_SETTING.MOBILE_UPLOADS_ENABLED}
                  >
                    <FormattedMessage {...localizedStrings.MOBILE_ENABLED} />
                  </label>
                </div>
                <div style={{ height: '16px' }} />
              </div>

              <div
                style={{
                  marginLeft: '10px',
                  paddingLeft: '2.5rem',
                }}
              >
                <div
                  className="field is-flex"
                  style={{
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <label className="label is-medium">
                      {formatMessage(localizedStrings.MOBILE_FOLDER_VERBIAGE)}
                    </label>
                  </div>
                  <a
                    className="button"
                    disabled={!isMobileEnabled || isStopped}
                    onClick={this.handleBrowseForDirectory}
                    id={GLOBAL_SETTING.MOBILE_UPLOADS_FOLDER}
                    data-test-name={GLOBAL_SETTING.MOBILE_UPLOADS_FOLDER}
                  >
                    {formatMessage(localizedStrings.CHANGE)}
                  </a>
                </div>
                <div className="field">
                  <div className="control">
                    <div className="is-indented-large has-text-black">
                      {globalSettings[GLOBAL_SETTING.MOBILE_UPLOADS_FOLDER]}
                    </div>
                  </div>
                </div>
                <div style={{ height: '32px' }} />
              </div>

              <div>
                <div
                  className="field is-flex"
                  style={{
                    justifyContent: 'left',
                  }}
                >
                  <input
                    className="is-checkradio is-medium"
                    data-test-name="mobile-create-upload-catalog"
                    id={GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG}
                    name={GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG}
                    type="checkbox"
                    disabled={!isMobileEnabled || hasUploadCatalog || isStopped}
                    onChange={this.handleToggleCheckbox}
                    checked={hasUploadCatalog}
                  />
                  <label
                    className="label is-medium"
                    htmlFor={GLOBAL_SETTING.MOBILE_UPLOADS_CATALOG}
                  >
                    <FormattedMessage
                      {...localizedStrings.MOBILE_CREATE_CATALOG_VERBIAGE}
                    />
                  </label>
                </div>
              </div>
              {activePanelId === BROWSE_SERVER_FOLDERS_PANEL_ID && (
                <BrowseServerFoldersDialog
                  onPathSelected={this.handlePathSelected}
                  showFiles={false}
                />
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    globalSettings: getGlobalSettings(state),
    activePanelId: getActivePanelId(state),
    serverStatus: getServerStatus(state),
    currentLocale: getLocale(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      getActivePanelId,
      getGlobalSettings,
      setMobileUploadSettings,
      showDialogPanel,
    })
)(injectIntl(MobilePage));



// WEBPACK FOOTER //
// ./src/view/Pages/GlobalSettingsPages/MobilePage.js